import React, { useState, useEffect, useContext, useRef } from 'react';
import { InvestmentIndex } from '../../../model/CalculatorInputs';
import { pipe } from 'fp-ts/lib/pipeable';
import { fold, Option } from 'fp-ts/lib/Option';
import { CalculatorStepsContext } from '../../Store/CalculatorStepsContext';


interface Props {
    index: number;

    investmentIndex: Option<InvestmentIndex>;
    availableInvestmentIndices: Array<InvestmentIndex>;
    invalid: boolean;
    isDuplicateStrategy: boolean;
    validationMessageId: string | undefined;
    validationIssues: Array<string>;
    selectInvestmentIndex: (code: string) => void;
}


export const SelectIndex = (props: Props) => {

    const initialIndexCode = pipe(props.investmentIndex,
        fold(() => 'none',
            (invIdx) => invIdx.code));

    const [indexCode, setIndexCode] = useState<string>(initialIndexCode);
    const indexNumber = props.index + 1;

    const availableIndexCodes = props.availableInvestmentIndices.map((invIdx) => invIdx.code);
    const selectRef = useRef<HTMLSelectElement>(null);
    const hasAvailableIndices = availableIndexCodes.length > 0;

    const { steps } = useContext(CalculatorStepsContext);

    useEffect(() => {
        setIndexCode(initialIndexCode);
    }, [initialIndexCode]);

    /* if the selected investment index is not available when the strategy switches out - deselect it */
    useEffect(() => {
        if (indexCode !== 'none' && !availableIndexCodes.includes(indexCode)) {
            props.selectInvestmentIndex('none');
        }

        /* if there is only one InvestmentIndex available, automatically select */
        if (availableIndexCodes.length === 1) {
            const onlyCode = availableIndexCodes[0];
            if (indexCode !== onlyCode) {
                props.selectInvestmentIndex(availableIndexCodes[0])
            }
        }
    }, [availableIndexCodes]);


    useEffect(() => {
        const grabFocus = async () => {
            pipe(steps.firstValidationError,
                fold(() => { return; },
                    (firstError) => {
                        if (props.validationIssues.includes(firstError)) {
                            if (selectRef.current) {
                                selectRef.current.focus();
                            }
                        }
                    }));
        };
        setTimeout(grabFocus, 25);
    }, [steps.firstValidationError])

    const indexIds = props.availableInvestmentIndices.map(invIdx => `rila-calculator-strategy-${indexNumber}-index-${invIdx.code}`)
    const firstIndexLabel = indexIds.length > 0 ? <label htmlFor={indexIds[0]}><span className="sr-only">Strategy {indexNumber} Investment </span>Index</label>
        : <label><span className="sr-only">Strategy {indexNumber} Investment </span>Index</label>;

    const indexLabel = (props.index === 0) ? firstIndexLabel : null;
    const strategySelectorClasses = ["step-4-row", `strategy-row-${indexNumber}`, "index-selector", "index-row", `index-selector-${indexNumber}`];





    const toggleRowClassList = ["index-select-row"];
    if (props.invalid) {
        toggleRowClassList.push("validation-error");
    }
    if (props.isDuplicateStrategy) {
        toggleRowClassList.push("duplicate-strategy");
    }

    const indexAriaLabel = `Index ${indexNumber}`;
    const toggleRowClasses = toggleRowClassList.join(" ");
    const indexId = `rila-calculator-index-selector-${indexNumber}`;
    const describedBy = props.invalid ? (props.validationMessageId) : undefined;


    function handleSelectionChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const selectedCode = event.target.value;
        setIndexCode(selectedCode);
        props.selectInvestmentIndex(selectedCode)
    }

    return (
        <div className={strategySelectorClasses.join(' ')}>

            {indexLabel}
            <fieldset>
                <legend className="sr-only">Strategy {indexNumber} Investment Index</legend>
                <div className={toggleRowClasses} aria-describedby={describedBy} >
                    {hasAvailableIndices ? <select id={indexId}
                        aria-label={indexAriaLabel}
                        aria-describedby={describedBy}
                        data-testid={indexId}
                        className="form-control"
                        value={indexCode}
                        ref={selectRef}
                        onChange={handleSelectionChange}
                        autoComplete="off">
                        {indexCode === 'none' ?
                            <option value="none">Select an index</option> : null}
                        {props.availableInvestmentIndices.map((invIdx) =>
                            <option value={invIdx.code} key={invIdx.code} dangerouslySetInnerHTML={{__html: invIdx.label}}></option>
                        )}
                    </select> : null}
                </div>
            </fieldset>
        </div>
    )
}

